import { useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import profileImage from '@renderer/assets/images/profile-image-placeholder.png';
import { GlobalSearchModal } from '@renderer/app/layout/global-search-modal';
import { QuickAddModal } from '@renderer/app/layout/quick-add-modal';
import { TrashModal } from '@renderer/app/layout/trash-modal';
import { navigationItems } from '@renderer/app/navigation/navigation-config';
import { getShellRouteMetadata } from '@renderer/app/routes/shell-route-metadata';
import { dispatchShellDeleteRequest } from '@renderer/app/events/shell-delete-events';
import { useSearchStore } from '@renderer/app/state/search-store';
import { useQuickAddStore, type QuickAddDomain } from '@renderer/app/state/quick-add-store';
import { useShellStore } from '@renderer/app/state/shell-store';
import { runLastUndoFromRenderer } from '@renderer/app/undo/undo-toast';
import { useContractsViewStore } from '@renderer/domains/contracts/contracts-view-store';
import { useCustomersViewStore } from '@renderer/domains/customers/customers-view-store';
import { useInvoicesViewStore } from '@renderer/domains/invoices/invoices-view-store';
import { useLeadsViewStore } from '@renderer/domains/leads/leads-view-store';
import { useProjectsViewStore } from '@renderer/domains/projects/projects-view-store';
import { ToastHost } from '@renderer/components/ui/ToastHost';
import { Icon } from '@renderer/shared/components/icons';
import { Breadcrumbs, IconButton, SearchTrigger } from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { isTextEditingElement } from '@renderer/shared/utils/is-text-editing-element';

type ShellAction = {
  id: 'notification' | 'create' | 'delete' | 'sync';
  icon: 'bell' | 'plus' | 'trash' | 'sync';
  label: string;
  disabled: boolean;
  onClick?: () => void;
};

function getQuickAddDomainFromPath(pathname: string): QuickAddDomain {
  if (pathname.startsWith('/projects')) return 'projects';
  if (pathname.startsWith('/leads')) return 'leads';
  if (pathname.startsWith('/tasks')) return 'tasks';
  if (pathname.startsWith('/customers')) return 'customers';
  if (pathname.startsWith('/resources')) return 'resources';
  if (pathname.startsWith('/contracts')) return 'contracts';
  if (pathname.startsWith('/invoices')) return 'invoices';
  return 'projects';
}

export function AppShell({ children }: PropsWithChildren): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isTrashOpen, setIsTrashOpen] = useState(false);
  const { preferences, routeDetailTitle, savePreferences } = useShellStore();
  const openGlobalSearch = useSearchStore((state) => state.open);
  const openQuickAdd = useQuickAddStore((state) => state.open);
  const { t } = useI18n();
  const projectSelectionIds = useProjectsViewStore((state) => state.selectionSession.selectedIds);
  const leadSelectionIds = useLeadsViewStore((state) => state.selectionSession.selectedIds);
  const customerSelectionIds = useCustomersViewStore((state) => state.selectionSession.selectedIds);
  const contractSelectionIds = useContractsViewStore((state) => state.selectionSession.selectedIds);
  const invoiceSelectionIds = useInvoicesViewStore((state) => state.selectionSession.selectedIds);

  const primaryItems = useMemo(() => navigationItems.filter((item) => item.kind === 'primary'), []);
  const secondaryItems = useMemo(() => navigationItems.filter((item) => item.kind === 'secondary'), []);
  const route = useMemo(() => getShellRouteMetadata(location.pathname, routeDetailTitle), [location.pathname, routeDetailTitle]);
  const breadcrumbs = useMemo(
    () =>
      route.breadcrumbs.map((item) =>
        item.to
          ? {
              label: item.label ?? t(item.labelKey ?? route.labelKey),
              to: item.to
            }
          : {
              label: item.label ?? t(item.labelKey ?? route.labelKey)
            }
      ),
    [route, t]
  );

  const deleteRequest = useMemo(() => {
    if (!route.deleteDomain) {
      return null;
    }

    switch (route.deleteDomain) {
      case 'projects':
        return {
          domain: route.deleteDomain,
          ids: route.mode === 'collection' ? projectSelectionIds : route.detailId ? [route.detailId] : [],
          source: route.mode === 'collection' ? 'selection' : 'current'
        } as const;
      case 'leads':
        return {
          domain: route.deleteDomain,
          ids: route.mode === 'collection' ? leadSelectionIds : route.detailId ? [route.detailId] : [],
          source: route.mode === 'collection' ? 'selection' : 'current'
        } as const;
      case 'tasks':
        return {
          domain: route.deleteDomain,
          ids: [] as string[],
          source: 'selection' as const
        };
      case 'customers':
        return {
          domain: route.deleteDomain,
          ids: route.mode === 'collection' ? customerSelectionIds : route.detailId ? [route.detailId] : [],
          source: route.mode === 'collection' ? 'selection' : 'current'
        } as const;
      case 'contracts':
        return {
          domain: route.deleteDomain,
          ids: route.mode === 'collection' ? contractSelectionIds : route.detailId ? [route.detailId] : [],
          source: route.mode === 'collection' ? 'selection' : 'current'
        } as const;
      case 'invoices':
        return {
          domain: route.deleteDomain,
          ids: route.mode === 'collection' ? invoiceSelectionIds : route.detailId ? [route.detailId] : [],
          source: route.mode === 'collection' ? 'selection' : 'current'
        } as const;
      case 'resources':
        return {
          domain: route.deleteDomain,
          ids: route.mode === 'collection' ? [] : route.detailId ? [route.detailId] : [],
          source: route.mode === 'collection' ? 'selection' : 'current'
        } as const;
      default:
        return null;
    }
  }, [contractSelectionIds, customerSelectionIds, invoiceSelectionIds, leadSelectionIds, projectSelectionIds, route]);

  useEffect(() => {
    setIsProfileMenuOpen(false);
  }, [location.pathname]);

  function handleDeleteRequest(): void {
    if (!deleteRequest || deleteRequest.ids.length === 0) {
      return;
    }

    dispatchShellDeleteRequest(deleteRequest);
  }

  const contextActions = useMemo<ShellAction[]>(
    () =>
      route.actionIds.map((actionId) => {
        if (actionId === 'delete') {
          return {
            id: actionId,
            icon: 'trash',
            label: t('shell.trash'),
            disabled: false,
            onClick: () => setIsTrashOpen(true)
          };
        }

        if (actionId === 'sync') {
          return {
            id: actionId,
            icon: 'sync',
            label: t('shell.sync'),
            disabled: true
          };
        }

        if (actionId === 'create') {
          return {
            id: actionId,
            icon: 'plus',
            label: t('shell.create'),
            disabled: false,
            onClick: () => openQuickAdd(getQuickAddDomainFromPath(location.pathname))
          };
        }

        return {
          id: actionId,
          icon: 'bell',
          label: t('shell.notification'),
          disabled: true
        };
      }),
    [deleteRequest, location.pathname, openQuickAdd, route.actionIds, t]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const searchState = useSearchStore.getState();
      const editing = isTextEditingElement(document.activeElement);

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();

        if (event.repeat) {
          return;
        }

        if (searchState.isOpen || useQuickAddStore.getState().isOpen || isTrashOpen) {
          return;
        }

        void useShellStore.getState().editorSaveHandler?.();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && event.key.toLowerCase() === 'z' && !editing) {
        event.preventDefault();

        if (event.repeat) {
          return;
        }

        if (searchState.isOpen || useQuickAddStore.getState().isOpen || isTrashOpen) {
          return;
        }

        void runLastUndoFromRenderer();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k' && !editing) {
        event.preventDefault();
        searchState.open();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'n' && !editing) {
        event.preventDefault();
        openQuickAdd(getQuickAddDomainFromPath(location.pathname));
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'o' && !editing) {
        event.preventDefault();
        searchState.open();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && !editing) {
        const keyAsNumber = Number(event.key);
        if (Number.isInteger(keyAsNumber) && keyAsNumber >= 1 && keyAsNumber <= primaryItems.length) {
          event.preventDefault();
          const targetItem = primaryItems[keyAsNumber - 1];
          if (targetItem) {
            void navigate(targetItem.path);
          }
          return;
        }

        const togglesSidebar = event.code === 'Semicolon' || event.key === '\u0219' || event.key === ';';
        if (togglesSidebar) {
          event.preventDefault();
          const shell = useShellStore.getState();
          void shell.savePreferences({ sidebarHidden: !shell.preferences.sidebarHidden });
          return;
        }
      }

      if (event.key === 'Escape') {
        if (searchState.isOpen) {
          event.preventDefault();
          searchState.close();
          return;
        }

        if (!editing && route.parentPath) {
          event.preventDefault();
          void navigate(route.parentPath);
        }

        return;
      }

      if ((event.key === 'Delete' || event.key === 'Backspace') && !editing && !event.metaKey && !event.ctrlKey && !event.altKey) {
        if (!deleteRequest || deleteRequest.ids.length === 0) {
          return;
        }

        event.preventDefault();
        handleDeleteRequest();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [deleteRequest, isTrashOpen, location.pathname, navigate, openQuickAdd, primaryItems, route.parentPath]);

  return (
    <div
      className={[
        'av-shell',
        preferences.sidebarHidden ? 'is-sidebar-hidden' : '',
        preferences.sidebarCollapsed ? 'is-sidebar-collapsed' : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <aside
        className={[
          'av-sidebar',
          preferences.sidebarHidden ? 'is-hidden' : '',
          preferences.sidebarCollapsed ? 'is-collapsed' : ''
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="av-sidebar__brand">
          <div className="av-sidebar__brand-mark">
            <Icon name="alttvision" />
          </div>
          {!preferences.sidebarCollapsed ? (
            <div className="av-sidebar__brand-copy">
              <strong>Alttvision</strong>
            </div>
          ) : null}
        </div>

        <nav className="av-sidebar__nav">
          {primaryItems.map((item) => (
            <NavLink
              key={item.id}
              className={({ isActive }) => ['av-sidebar__link', isActive ? 'is-active' : ''].filter(Boolean).join(' ')}
              to={item.path}
            >
              <Icon name={item.icon} />
              {!preferences.sidebarCollapsed ? <span>{t(item.labelKey)}</span> : null}
            </NavLink>
          ))}
        </nav>

        <div className="av-sidebar__secondary">
          {secondaryItems.map((item) => (
            <NavLink
              key={item.id}
              className={({ isActive }) => ['av-sidebar__link', isActive ? 'is-active' : ''].filter(Boolean).join(' ')}
              to={item.path}
            >
              <Icon name={item.icon} />
              {!preferences.sidebarCollapsed ? <span>{t(item.labelKey)}</span> : null}
            </NavLink>
          ))}
        </div>

        <button
          className="av-sidebar__toggle"
          type="button"
          onClick={() => void savePreferences({ sidebarCollapsed: !preferences.sidebarCollapsed })}
        >
          {preferences.sidebarCollapsed ? '>' : '<'}
        </button>
      </aside>

      <div className="av-shell__main">
        <TrashModal isOpen={isTrashOpen} onClose={() => setIsTrashOpen(false)} />
        <header className="av-topbar">
          <div className="av-topbar__identity">
            <Breadcrumbs items={breadcrumbs} />
          </div>
          <div className="av-topbar__center">
            <SearchTrigger label={t('shell.searchPlaceholder')} shortcut={t('shell.searchShortcut')} onClick={openGlobalSearch} />
          </div>
          <div className="av-topbar__actions">
            {contextActions.map((action) => (
              <IconButton
                key={`${location.pathname}-${action.id}`}
                disabled={action.disabled}
                icon={action.icon}
                label={action.label}
                onClick={action.onClick}
              />
            ))}

            <div className="av-avatar-menu">
              <button className="av-avatar-menu__trigger" type="button" onClick={() => setIsProfileMenuOpen((value) => !value)}>
                <img alt={t('shell.profile')} className="av-avatar-menu__image" src={profileImage} />
              </button>
              {isProfileMenuOpen ? (
                <div className="av-avatar-menu__panel">
                  <button type="button">{t('shell.profile')}</button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      void navigate('/settings');
                    }}
                  >
                    {t('shell.settings')}
                  </button>
                  <button type="button">{t('shell.signOut')}</button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <main className="av-shell__content">{children}</main>
      </div>
      <GlobalSearchModal />
      <QuickAddModal />
      <ToastHost />
    </div>
  );
}
